import { PowerPlant, PowerPlantType } from "../../types/powerPlants";
import { CalculationResult } from "../../types/calculations";
import { PowerPlantStats } from "../../types/calculations";
import {
  POWER_PLANTS,
  getPowerPlantSpec,
  POWER_REQUIREMENTS,
} from "../../constants/powerPlants";
import { Drive } from "../../types/drive";

/**
 * Calculates power plant statistics including maintenance costs
 * @param powerPlant - The power plant configuration
 * @param shipSize - The size of the ship in tons
 * @param drives - Array of drives to calculate power requirements
 * @returns Calculation result containing power plant stats or an error
 */
export const calculatePowerPlantStats = (
  powerPlant: PowerPlant,
  shipSize: number,
  drives: Drive[] = [],
): CalculationResult<PowerPlantStats> => {
  // Validate inputs
  if (shipSize <= 0) {
    return {
      success: false,
      error: {
        type: "INVALID_INPUT",
        message: "Ship size must be positive",
      },
    };
  }

  if (
    !powerPlant.type ||
    !POWER_PLANTS[powerPlant.type as keyof typeof POWER_PLANTS]
  ) {
    return {
      success: false,
      error: {
        type: "INVALID_INPUT",
        message: "Invalid power plant type",
      },
    };
  }

  // Calculate power requirements according to Traveller rules
  const basicSystemsPower = shipSize * POWER_REQUIREMENTS.BASIC_SYSTEMS.PERCENT;

  let maneuverDrivePower = 0;
  const maneuverDrive = drives.find((drive) => drive.type === "maneuver" && drive.role === 'primary');
  if (maneuverDrive) {
    const thrustMultiplier =
      maneuverDrive.rating === 0
        ? POWER_REQUIREMENTS.MANEUVER_DRIVE.THRUST_0_MULTIPLIER
        : 1;
    maneuverDrivePower =
      shipSize *
      POWER_REQUIREMENTS.MANEUVER_DRIVE.PERCENT *
      maneuverDrive.rating *
      thrustMultiplier;
  }

  let jumpDrivePower = 0;
  const jumpDrive = drives.find((drive) => drive.type === "jump" && drive.role === 'primary');
  if (jumpDrive) {
    jumpDrivePower =
      shipSize * POWER_REQUIREMENTS.JUMP_DRIVE.PERCENT * jumpDrive.rating;
  }

  const totalPowerRequired =
    basicSystemsPower + maneuverDrivePower + jumpDrivePower;

  // Calculate power plant size needed
  const spec = getPowerPlantSpec(powerPlant.type);
  const tons =
    powerPlant.weight > 0
      ? powerPlant.weight
      : Math.ceil(totalPowerRequired / spec.powerPerTon);
  const cost = powerPlant.cost > 0 ? powerPlant.cost : tons * spec.costPerTon;
  const power = tons * spec.powerPerTon;
  const maintenanceCost =
    powerPlant.maintenanceCost > 0
      ? powerPlant.maintenanceCost
      : tons * spec.maintenanceCost;

  // Validate maintenance cost
  if (maintenanceCost < 0) {
    return {
      success: false,
      error: {
        type: "CALCULATION_ERROR",
        message: "Invalid maintenance cost calculated",
      },
    };
  }

  return {
    success: true,
    data: {
      cost,
      tons,
      power,
      maintenanceCost,
      powerRequired: totalPowerRequired,
    },
  };
};

export const calculatePowerRequirements = (
  shipSize: number,
  drives: Drive[],
) => {
  // Basic ship systems: 20% of hull tonnage
  const basicSystemsPower = shipSize * POWER_REQUIREMENTS.BASIC_SYSTEMS.PERCENT;

  // Maneuver drive: 10% of hull tonnage × thrust rating (only use primary drive for power)
  let maneuverDrivePower = 0;
  const maneuverDrive = drives.find((drive) => drive.type === "maneuver" && drive.role === 'primary');
  if (maneuverDrive) {
    const thrustMultiplier =
      maneuverDrive.rating === 0
        ? POWER_REQUIREMENTS.MANEUVER_DRIVE.THRUST_0_MULTIPLIER
        : 1;
    maneuverDrivePower =
      shipSize *
      POWER_REQUIREMENTS.MANEUVER_DRIVE.PERCENT *
      maneuverDrive.rating *
      thrustMultiplier;
  }

  // Jump drive: 10% of hull tonnage × jump rating (fusion/antimatter only) (only use primary drive for power)
  let jumpDrivePower = 0;
  const jumpDrive = drives.find((drive) => drive.type === "jump" && drive.role === 'primary');
  if (jumpDrive) {
    jumpDrivePower =
      shipSize * POWER_REQUIREMENTS.JUMP_DRIVE.PERCENT * jumpDrive.rating;
  }

  const totalPowerRequired =
    basicSystemsPower + maneuverDrivePower + jumpDrivePower;

  // Calculate operational modes
  const normalOperations = basicSystemsPower + maneuverDrivePower; // Basic + M-Drive
  const jumpOperations = basicSystemsPower + jumpDrivePower; // Basic + J-Drive
  const maximumLoad = totalPowerRequired; // All systems (rare)

  return {
    basicSystems: basicSystemsPower,
    maneuverDrive: maneuverDrivePower,
    jumpDrive: jumpDrivePower,
    total: totalPowerRequired,
    normalOperations,
    jumpOperations,
    maximumLoad,
  };
};

/**
 * Calculates the appropriate power plant for a given power requirement
 * @param params - Parameters for power plant calculation
 * @returns Calculation result containing power plant configuration or an error
 */
export const calculatePowerPlantRequirements = (params: {
  powerRequired: number;
  shipSize: number;
  techLevel: number;
}): CalculationResult<PowerPlant> => {
  const { powerRequired, shipSize, techLevel } = params;

  // Validate inputs
  if (powerRequired <= 0) {
    return {
      success: false,
      error: {
        type: "INVALID_INPUT",
        message: "Power requirement must be positive",
      },
    };
  }

  if (shipSize <= 0) {
    return {
      success: false,
      error: {
        type: "INVALID_INPUT",
        message: "Ship size must be positive",
      },
    };
  }

  // Find the smallest power plant that can meet the requirements
  let bestPowerPlant: PowerPlant | null = null;
  let minTons = Infinity;

  // Check each power plant type
  for (const [type, spec] of Object.entries(POWER_PLANTS)) {
    // Skip if tech level is too low
    if (spec.techLevel > techLevel) {
      continue;
    }

    // Calculate tons needed for this power plant type
    const tonsNeeded = Math.ceil(powerRequired / spec.powerPerTon);
    const cost = tonsNeeded * spec.costPerTon * 1000000; // Convert MCr to Cr
    const powerOutput = tonsNeeded * spec.powerPerTon;
    const maintenanceCost = tonsNeeded * spec.maintenanceCost * 1000000; // Convert MCr to Cr

    // Check if this is better than our current best
    if (tonsNeeded < minTons || (tonsNeeded === minTons && cost < (bestPowerPlant?.cost || Infinity))) {
      bestPowerPlant = {
        type: type as PowerPlantType,
        rating: 0, // Power plants don't have ratings
        cost,
        weight: tonsNeeded,
        power: powerOutput,
        techLevel: spec.techLevel,
        maintenanceCost,
        description: spec.description,
      };
      minTons = tonsNeeded;
    }
  }

  if (!bestPowerPlant) {
    return {
      success: false,
      error: {
        type: "CALCULATION_ERROR",
        message: "No suitable power plant found for the given requirements",
      },
    };
  }

  return {
    success: true,
    data: bestPowerPlant,
  };
};
